import type {
  UpgradeState,
  UpgradeModifiers,
  UpgradeNodeStatus,
  UpgradeColumn,
  UpgradeBranch,
  UpgradeDefinition,
} from "@/types/upgrade.types"
import type { DefenseType } from "@/types/hero.types"
import { upgradeDefinitions, getRootUpgrade, getBranchUpgrades } from "@/data/upgrade-tree"

/**
 * Calculate cost for the next purchase of an upgrade
 * Cost doubles with each purchase: baseCost * 2^purchaseCount
 */
export function getUpgradeCost(upgrade: UpgradeDefinition, currentPurchaseCount: number): number {
  return upgrade.cost * Math.pow(2, currentPurchaseCount)
}

/**
 * Calculate total bonus from purchase count using diminishing returns
 * Each purchase adds: baseBonus * 0.75^purchaseIndex
 * Example: 10% base → 10%, 17.5%, 23.125%, 27.34%, 30.51% total
 */
export function getTotalBonus(baseValue: number, purchaseCount: number): number {
  let total = 0
  for (let i = 0; i < purchaseCount; i++) {
    total += baseValue * Math.pow(0.75, i)
  }
  return total
}

/**
 * Get purchase count for an upgrade
 */
export function getPurchaseCount(upgradeId: string, upgradeState: UpgradeState): number {
  return upgradeState.purchaseCounts[upgradeId] ?? 0
}

/**
 * Get the status of an upgrade node based on current state
 */
export function getUpgradeNodeStatus(
  upgrade: UpgradeDefinition,
  upgradeState: UpgradeState
): UpgradeNodeStatus {
  const purchaseCount = getPurchaseCount(upgrade.id, upgradeState)

  // Check if maxed out
  if (purchaseCount >= upgrade.maxPurchases) {
    return "maxed"
  }

  // Already purchased at least once - can upgrade more if not maxed
  if (purchaseCount > 0) {
    return "unlocked"
  }

  // Root upgrade - always available if not purchased
  if (upgrade.tier === 0) {
    return "available"
  }

  // Check prerequisites - root must be purchased
  const rootUpgrade = getRootUpgrade(upgrade.column)
  if (!rootUpgrade || getPurchaseCount(rootUpgrade.id, upgradeState) === 0) {
    return "locked"
  }

  // Check tier prerequisites (must purchase previous tier in same branch)
  if (upgrade.tier > 1 && upgrade.branch) {
    const branchUpgrades = getBranchUpgrades(upgrade.column, upgrade.branch)
    const previousTierUpgrade = branchUpgrades.find((u) => u.tier === upgrade.tier - 1)
    if (previousTierUpgrade && getPurchaseCount(previousTierUpgrade.id, upgradeState) === 0) {
      return "locked"
    }
  }

  return "available"
}

/**
 * Check if an upgrade can be purchased (first time or additional levels)
 */
export function canPurchaseUpgrade(
  upgradeId: string,
  upgradeState: UpgradeState,
  currentMoney: number
): boolean {
  const upgrade = upgradeDefinitions[upgradeId]
  if (!upgrade) return false

  const purchaseCount = getPurchaseCount(upgradeId, upgradeState)

  // Check if already maxed
  if (purchaseCount >= upgrade.maxPurchases) return false

  const status = getUpgradeNodeStatus(upgrade, upgradeState)
  // Can purchase if available OR unlocked (not yet maxed)
  if (status !== "available" && status !== "unlocked") return false

  const cost = getUpgradeCost(upgrade, purchaseCount)
  return currentMoney >= cost
}

/**
 * Purchase an upgrade (increment purchase count) and return the new state
 */
export function purchaseUpgrade(
  upgradeId: string,
  upgradeState: UpgradeState
): UpgradeState {
  const upgrade = upgradeDefinitions[upgradeId]
  if (!upgrade) return upgradeState

  const currentCount = getPurchaseCount(upgradeId, upgradeState)

  return {
    ...upgradeState,
    purchaseCounts: {
      ...upgradeState.purchaseCounts,
      [upgradeId]: currentCount + 1,
    },
  }
}

// Legacy alias for backward compatibility
export const unlockUpgrade = purchaseUpgrade
export const canUnlockUpgrade = canPurchaseUpgrade

/**
 * Calculate all upgrade modifiers from current state
 * Now uses purchaseCounts with diminishing returns
 */
export function getUpgradeModifiers(upgradeState: UpgradeState): UpgradeModifiers {
  const modifiers: UpgradeModifiers = {
    allTowerDamage: 1.0,
    archerDamage: 1.0,
    archerRange: 1.0,
    archerSpeed: 1.0,
    barracksSpawnCount: 0,
    soldierHp: 1.0,
    mageDamage: 1.0,
    siegeDamage: 1.0,
    heroSpeed: 1.0,
    heroCooldown: 1.0,
    heroHp: 1.0,
    shopDiscount: 0,
    mapRevealNodes: 0,
    soldierDefenseType: "light",
    soldierDamageMultiplier: 1.0,
    soldierArmorMultiplier: 1.0,
  }

  // Iterate over all purchased upgrades
  for (const [upgradeId, purchaseCount] of Object.entries(upgradeState.purchaseCounts)) {
    if (purchaseCount <= 0) continue

    const upgrade = upgradeDefinitions[upgradeId]
    if (!upgrade) continue

    for (const effect of upgrade.effects) {
      // Skip ability effects (handled separately)
      if (effect.ability) continue

      // Handle first-purchase-only effects (like armor type)
      if (effect.isFirstPurchaseOnly && purchaseCount >= 1) {
        if (effect.stat === "defenseType" && typeof effect.value === "string") {
          // Heavy armor overrides medium
          const armorPriority: Record<string, number> = { light: 0, medium: 1, heavy: 2 }
          const currentPriority = armorPriority[modifiers.soldierDefenseType] ?? 0
          const newPriority = armorPriority[effect.value] ?? 0
          if (newPriority > currentPriority) {
            modifiers.soldierDefenseType = effect.value as DefenseType
          }
        }
        continue
      }

      // For non-first-purchase-only effects, apply with diminishing returns
      const baseValue = typeof effect.value === "number" ? effect.value : 1

      switch (effect.target) {
        case "all-towers":
          if (effect.stat === "damage") {
            // Multiplier effect with diminishing returns
            const bonus = getTotalBonus(baseValue - 1, purchaseCount)
            modifiers.allTowerDamage *= (1 + bonus)
          }
          break

        case "archer":
          if (effect.stat === "damage") {
            const bonus = getTotalBonus(baseValue - 1, purchaseCount)
            modifiers.archerDamage *= (1 + bonus)
          } else if (effect.stat === "range") {
            const bonus = getTotalBonus(baseValue - 1, purchaseCount)
            modifiers.archerRange *= (1 + bonus)
          } else if (effect.stat === "attackSpeed") {
            const bonus = getTotalBonus(baseValue - 1, purchaseCount)
            modifiers.archerSpeed *= (1 + bonus)
          }
          break

        case "barracks":
          if (effect.stat === "spawnCount" && effect.isFlat) {
            // Flat bonus: apply diminishing returns
            modifiers.barracksSpawnCount += Math.floor(getTotalBonus(baseValue, purchaseCount))
          }
          break

        case "soldiers":
          if (effect.stat === "hp") {
            const bonus = getTotalBonus(baseValue - 1, purchaseCount)
            modifiers.soldierHp *= (1 + bonus)
          } else if (effect.stat === "damage") {
            const bonus = getTotalBonus(baseValue - 1, purchaseCount)
            modifiers.soldierDamageMultiplier *= (1 + bonus)
          }
          break

        case "mage":
          if (effect.stat === "damage") {
            const bonus = getTotalBonus(baseValue - 1, purchaseCount)
            modifiers.mageDamage *= (1 + bonus)
          }
          break

        case "siege":
          if (effect.stat === "damage") {
            const bonus = getTotalBonus(baseValue - 1, purchaseCount)
            modifiers.siegeDamage *= (1 + bonus)
          }
          break

        case "hero":
          if (effect.stat === "speed") {
            const bonus = getTotalBonus(baseValue - 1, purchaseCount)
            modifiers.heroSpeed *= (1 + bonus)
          } else if (effect.stat === "cooldown") {
            // Cooldown reduction: value is 0.85 for 15% reduction
            // Diminishing returns on the reduction amount
            const reductionPerPurchase = 1 - baseValue // e.g., 0.15 for 15% reduction
            const totalReduction = getTotalBonus(reductionPerPurchase, purchaseCount)
            modifiers.heroCooldown *= (1 - totalReduction)
          } else if (effect.stat === "hp") {
            const bonus = getTotalBonus(baseValue - 1, purchaseCount)
            modifiers.heroHp *= (1 + bonus)
          }
          break

        case "shop":
          if (effect.stat === "price" && effect.isFlat) {
            // Flat discount percentage
            modifiers.shopDiscount += getTotalBonus(baseValue, purchaseCount)
          }
          break

        case "map":
          if (effect.stat === "revealNodes" && effect.isFlat) {
            modifiers.mapRevealNodes += Math.floor(getTotalBonus(baseValue, purchaseCount))
          }
          break
      }
    }
  }

  return modifiers
}

/**
 * Check if an active ability is unlocked (purchased at least once)
 */
export function hasActiveAbility(
  abilityId: string,
  upgradeState: UpgradeState
): boolean {
  for (const [upgradeId, purchaseCount] of Object.entries(upgradeState.purchaseCounts)) {
    if (purchaseCount <= 0) continue

    const upgrade = upgradeDefinitions[upgradeId]
    if (!upgrade) continue

    for (const effect of upgrade.effects) {
      if (effect.ability === abilityId) {
        return true
      }
    }
  }
  return false
}

/**
 * Get all unlocked active abilities
 */
export function getUnlockedAbilities(upgradeState: UpgradeState): string[] {
  const abilities: string[] = []

  for (const [upgradeId, purchaseCount] of Object.entries(upgradeState.purchaseCounts)) {
    if (purchaseCount <= 0) continue

    const upgrade = upgradeDefinitions[upgradeId]
    if (!upgrade || upgrade.type !== "active") continue

    for (const effect of upgrade.effects) {
      if (effect.ability) {
        abilities.push(effect.ability)
      }
    }
  }

  return abilities
}

/**
 * Get the total gold spent on upgrades
 * Now accounts for multi-purchase costs
 */
export function getTotalUpgradeCost(upgradeState: UpgradeState): number {
  let total = 0

  for (const [upgradeId, purchaseCount] of Object.entries(upgradeState.purchaseCounts)) {
    if (purchaseCount <= 0) continue

    const upgrade = upgradeDefinitions[upgradeId]
    if (!upgrade) continue

    // Sum up costs for each purchase level
    for (let i = 0; i < purchaseCount; i++) {
      total += getUpgradeCost(upgrade, i)
    }
  }

  return total
}

/**
 * Get progress summary for a column
 */
export function getColumnProgress(
  column: UpgradeColumn,
  upgradeState: UpgradeState
): { unlocked: number; total: number; selectedBranch: UpgradeBranch } {
  const rootUpgrade = getRootUpgrade(column)
  const hasRoot = rootUpgrade ? getPurchaseCount(rootUpgrade.id, upgradeState) > 0 : false
  const selectedBranch = upgradeState.selectedBranches[column]

  let unlocked = hasRoot ? 1 : 0
  let total = 1 // Root

  if (selectedBranch) {
    const branchUpgrades = getBranchUpgrades(column, selectedBranch)
    total += branchUpgrades.length
    unlocked += branchUpgrades.filter((u) =>
      getPurchaseCount(u.id, upgradeState) > 0
    ).length
  } else {
    // Both branches available, count all as potential
    const branchA = getBranchUpgrades(column, "a")
    const branchB = getBranchUpgrades(column, "b")
    total += Math.max(branchA.length, branchB.length)
  }

  return { unlocked, total, selectedBranch }
}
